<template>
    <Head title="Create Location" />

    <div class="py-6">
        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-6">
                <Link
                    :href="route('settings.locations.index')"
                    class="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
                >
                    <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Locations
                </Link>
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">Create New Location</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">Add a new location to your organization</p>
                    </div>
                    <InfoButton title="Creating Locations" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            Create new physical locations to organize your assets and track work. Locations support hierarchical
                            structures (e.g., Building → Floor → Room), optional address information, and user access assignments.
                            After creating a location, you can assign users to grant them access for mobile use and asset filtering.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Name (required):</strong> Clear, descriptive name like "Main Office Building" or "Warehouse
                              Floor 2"<br>
                            • <strong>Code:</strong> Optional short identifier (e.g., BLDG-01, WH-F2) for quick reference<br>
                            • <strong>Type (required):</strong> Select Building, Floor, Room, Site, Warehouse, or Other<br>
                            • <strong>Parent Location:</strong> Optional - creates hierarchical structure. Leave as "None" for root
                              locations<br>
                            • <strong>Description:</strong> Brief note about the location's purpose or characteristics<br>
                            • <strong>Address Information:</strong> Optional physical address fields for mapping and reporting<br>
                            • <strong>Active Status:</strong> Checked = location appears in dropdowns; unchecked = hidden but not
                              deleted<br>
                            • <strong>User Assignment:</strong> After creation, you can assign users to grant them access to this
                              location and its assets
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Plan hierarchy first</strong> - Decide your structure before creating (Site > Building > Floor
                              > Room is common)<br>
                            • <strong>Use consistent naming</strong> - Follow a pattern like "Building A Floor 1" across all locations<br>
                            • <strong>Codes save time</strong> - Short codes make asset tagging easier (e.g., A-101 for Building A
                              Room 101)<br>
                            • <strong>Start with parent</strong> - Create parent locations before child locations for proper hierarchy<br>
                            • <strong>Address is optional</strong> - Only needed for root locations like sites or buildings<br>
                            • <strong>Active by default</strong> - Keep checked unless you're setting up for future use<br>
                            • <strong>Assign users after creation</strong> - Users cannot be assigned during creation - do this on
                              the location detail page after saving
                        </p>
                    </InfoButton>
                </div>
            </div>

            <!-- Form Card -->
            <form @submit.prevent="submitForm" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div class="p-6 space-y-6">
                    <!-- Basic Information Section -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Name -->
                            <div class="md:col-span-2">
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Name <span class="text-red-500">*</span>
                                </label>
                                <input
                                    v-model="form.name"
                                    type="text"
                                    id="name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="e.g., Main Office Building"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                            </div>

                            <!-- Code -->
                            <div>
                                <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Code
                                </label>
                                <input
                                    v-model="form.code"
                                    type="text"
                                    id="code"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="e.g., BLDG-01"
                                />
                                <p v-if="form.errors.code" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.code }}</p>
                            </div>

                            <!-- Type -->
                            <div>
                                <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Type <span class="text-red-500">*</span>
                                </label>
                                <select
                                    v-model="form.type"
                                    id="type"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="">Select Type</option>
                                    <option v-for="type in locationTypes" :key="type.value" :value="type.value">
                                        {{ type.label }}
                                    </option>
                                </select>
                                <p v-if="form.errors.type" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.type }}</p>
                            </div>

                            <!-- Parent Location -->
                            <div class="md:col-span-2">
                                <label for="parent_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Parent Location
                                </label>
                                <select
                                    v-model="form.parent_id"
                                    id="parent_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option :value="null">None (Root Location)</option>
                                    <option v-for="location in parentLocations" :key="location.id" :value="location.id">
                                        {{ location.path_name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Select a parent location to create a hierarchical structure</p>
                                <p v-if="form.errors.parent_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.parent_id }}</p>
                            </div>

                            <!-- Description -->
                            <div class="md:col-span-2">
                                <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Description
                                </label>
                                <textarea
                                    v-model="form.description"
                                    id="description"
                                    rows="3"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="Brief description of this location..."
                                ></textarea>
                                <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.description }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Address Section -->
                    <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Address Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Address Line 1 -->
                            <div class="md:col-span-2">
                                <label for="address_line_1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Address Line 1
                                </label>
                                <input
                                    v-model="form.address_line_1"
                                    type="text"
                                    id="address_line_1"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.address_line_1" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.address_line_1 }}</p>
                            </div>

                            <!-- Address Line 2 -->
                            <div class="md:col-span-2">
                                <label for="address_line_2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Address Line 2
                                </label>
                                <input
                                    v-model="form.address_line_2"
                                    type="text"
                                    id="address_line_2"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.address_line_2" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.address_line_2 }}</p>
                            </div>

                            <!-- City -->
                            <div>
                                <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    City
                                </label>
                                <input
                                    v-model="form.city"
                                    type="text"
                                    id="city"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.city" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.city }}</p>
                            </div>

                            <!-- State -->
                            <div>
                                <label for="state" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    State/Province
                                </label>
                                <input
                                    v-model="form.state"
                                    type="text"
                                    id="state"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.state" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.state }}</p>
                            </div>

                            <!-- Postal Code -->
                            <div>
                                <label for="postal_code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Postal Code
                                </label>
                                <input
                                    v-model="form.postal_code"
                                    type="text"
                                    id="postal_code"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.postal_code" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.postal_code }}</p>
                            </div>

                            <!-- Country -->
                            <div>
                                <label for="country" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Country
                                </label>
                                <input
                                    v-model="form.country"
                                    type="text"
                                    id="country"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.country" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.country }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Status -->
                    <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <div class="flex items-center">
                            <input
                                v-model="form.is_active"
                                type="checkbox"
                                id="is_active"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                            />
                            <label for="is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                Active
                            </label>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Inactive locations won't appear in dropdown lists</p>
                    </div>

                    <!-- User Assignment Info Box -->
                    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>User Assignment:</strong> Users can be assigned to this location after it has been created. Once saved, visit the location's detail page to assign users and grant them access to assets at this location. You can also choose whether users should automatically have access to child locations (include descendants option).
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 rounded-b-lg flex justify-end space-x-3">
                    <Link
                        :href="route('settings.locations.index')"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="form.processing">Creating...</span>
                        <span v-else>Create Location</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    parentLocations: Array,
    locationTypes: Array,
});

const form = useSecureForm({
    name: '',
    code: '',
    parent_id: null,
    type: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    description: '',
    is_active: true,
});

const submitForm = () => {
    form.post(route('settings.locations.store'));
};
</script>
